import { ClassificationsProviderV2 } from '@/providers/classification/classifications-v2.provider';
import { DashboardingProvider } from '@/providers/dashboarding.provider';
import { DashboardsProvider } from '@/providers/dashboards/dashboards.provider';
import { ProfileProvider } from '@/providers/profile/profile.provider';
import { ProjectsProvider } from '@/providers/projects/projects.provider';
import { EnvironmentService } from '@/utils/environment/environment.service';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, type OnInit, computed, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { filter, shareReplay, switchMap } from 'rxjs/operators';
import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '../advizeo-modal-spawner/advizeo-modal-spawner.component';

function getRouteLastChild(route: ActivatedRoute) {
    let child = route;
    while (child.firstChild) {
        child = child.firstChild;
    }
    return child;
}

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'debug-modal',
    templateUrl: './debug-modal.component.html',
    styleUrls: ['./debug-modal.component.scss'],
    imports: [AsyncPipe]
})
export class DebugModalComponent implements AdvizeoModalComponent<null, null>, OnInit {
    private _projectsProvider = inject(ProjectsProvider);
    private _profileProvider = inject(ProfileProvider);
    private _classificationsProvider = inject(ClassificationsProviderV2);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _dashboardsProvider = inject(DashboardsProvider);
    private _route = inject(ActivatedRoute);
    private _envService = inject(EnvironmentService);

    input = injectAdvizeoModalInput<null>();
    close = output<void>();

    title = signal('Informations de debug');
    isDismissable = signal(true);
    buttons = signal<AdvizeoModalButton[]>([]);

    project$ = this._projectsProvider.getUserCurrentProject$();
    user$ = this._profileProvider.getUser$();
    classificationNode$ = this._dashboardingProvider.getSelectedNodeId$().pipe(
        switchMap(nodeId => this._classificationsProvider.getClassificationNodeById$(nodeId)),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    dashboardId$ = this._dashboardingProvider.getSelectedDashboardId$();
    dashboard$ = this.dashboardId$.pipe(
        filter(isNotNullish),
        switchMap(dashboardId => this._dashboardsProvider.getDashboardName$(dashboardId))
    );
    period$ = this._dashboardingProvider.getSelectedPeriodIdentifier$();

    currentUrl = window.location.href;
    userAgent = navigator.userAgent;

    memoryInfo: string = 'Unavailable';

    routeData = toSignal(getRouteLastChild(this._route.root).data);
    routeTeam = computed(() => {
        const routeData = this.routeData();
        if (routeData && 'team' in routeData && typeof routeData.team === 'string') {
            return routeData.team;
        } else {
            return 'Non défini';
        }
    });

    version = this._envService.getVersion();

    ngOnInit() {
        const memory = (window.performance as any).memory;

        if (memory) {
            this.memoryInfo = `jsHeapSizeLimit=${memory.jsHeapSizeLimit}, totalJSHeapSize=${memory.totalJSHeapSize}, usedJSHeapSize=${memory.usedJSHeapSize}`;
        }
    }
}
